import { useContext, useEffect, useState } from 'react';
import moment from 'moment';
import { FiSend, FiTrash2 } from 'react-icons/fi';
import useAuthorityLevel from '@/hooks/useAuthorityLevel';
import { useSession } from '@/hooks/useSession';
import { ProjectsContext } from '../provider';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import { AvatarWrapper } from '@/components/ui/avatar';

// Comentário lúdico: card do comentário em branco com leve shadow de
// destaque; a resposta aninhada usa o fundo do Fórum (bg-bright) — diferente
// do Fórum, que ficou sem avatar, aqui mantém o avatar e o "há x tempo" ao
// lado do nome. Botão de enviar resposta no laranja do tema. A variante
// corporativa continua em ../projects-card-comment.jsx.
export function ProjectsCardCommentPlayful({ comment, comments, onDelete }) {
  const { session } = useSession();
  const client = useClient();
  const authorityLevel = useAuthorityLevel(session);
  const { selectedProject } = useContext(ProjectsContext);
  const [isOpenAnswer, setIsOpenAnswer] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [answerValue, setAnswerValue] = useState('');

  const findAnswers = () => {
    const commentsWithAnswers = comments?.filter(c => c?.['comment-parent-id'] !== null);
    const answersByCommentId = commentsWithAnswers?.filter(item => item?.['comment-parent-id'] === comment?.id);

    setAnswers(answersByCommentId);
  };

  const handleCreateAnswer = async value => {
    try {
      const response = await client.mutate('/comments', {
        'comment-parent-id': comment.id,
        body: value,
        'project-version-id': comment['project-version-id']
      });

      setAnswers(prev => [response.data, ...prev]);
    } catch (error) {
      toast.error('Erro ao responder');
    }
  };

  const handleDeleteAnswer = async answerId => {
    const { error } = await client.delete(`/comments/${answerId}`);

    if (!error) {
      setAnswers(prev => prev.filter(item => item.id !== answerId));
    } else {
      toast.error('Erro ao deletar resposta');
    }
  };

  const handleSubmitAnswer = () => {
    if (!answerValue.trim()) return;
    handleCreateAnswer(answerValue.trim());
    setAnswerValue('');
  };

  useEffect(() => {
    findAnswers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comments]);

  const canDeleteComment = (authorityLevel === 'admin' || comment.user?.id === session.user.id) && !selectedProject.approved;

  return (
    <div className="rounded-xl bg-white p-3 shadow-card">
      <div className="flex items-start gap-3">
        <AvatarWrapper
          size="sm"
          src={comment?.user?.image?.url}
          text={comment?.user?.name}
        />
        <div className="min-w-0 grow">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-extrabold text-neutral-900">{(comment.user && comment.user.name) || 'Usuário do sistema'}</span>
            <span className="text-xs text-neutral-dark">{moment(comment.created_at).fromNow()}</span>
          </div>
          <p className="mb-0 mt-0.5 break-words text-xs text-neutral-dark">{comment.body}</p>

          {isOpenAnswer && (
            <div className="mt-2 flex gap-2">
              <input
                type="text"
                value={answerValue}
                onChange={e => setAnswerValue(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') handleSubmitAnswer();
                }}
                placeholder="Respondendo"
                aria-label="Respondendo"
                className="min-w-0 grow rounded-full border border-neutral-lighter bg-white px-3 py-1.5 text-xs"
              />
              <button
                type="button"
                onClick={handleSubmitAnswer}
                aria-label="Enviar resposta"
                className="flex shrink-0 items-center justify-center rounded-full p-2 text-white"
                style={{ background: 'var(--color-mission)' }}
              >
                <FiSend className="h-3.5 w-3.5" />
              </button>
            </div>
          )}

          {answers?.length > 0 && (
            <div className="mt-2 flex flex-col gap-2">
              {answers.map(item => (
                <div
                  key={item.id}
                  className="rounded-xl bg-bright p-3"
                >
                  <div className="flex items-start gap-3">
                    <AvatarWrapper
                      size="sm"
                      src={item?.user?.image?.url}
                      text={item?.user?.name}
                    />
                    <div className="min-w-0 grow">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-extrabold text-neutral-900">{item?.user?.name}</span>
                        <span className="text-xs text-neutral-dark">{moment(item['created_at'] || item['created-at']).fromNow()}</span>
                      </div>
                      <p className="mb-0 mt-0.5 break-words text-xs text-neutral-dark">{item.body}</p>
                    </div>
                    {session.user.id === item?.user?.id && (
                      <button
                        type="button"
                        onClick={() => handleDeleteAnswer(item.id)}
                        aria-label="Excluir resposta"
                        className="shrink-0 text-neutral-dark transition-colors hover:text-danger"
                      >
                        <FiTrash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex shrink-0 flex-col items-end gap-2">
          <button
            type="button"
            onClick={() => setIsOpenAnswer(prev => !prev)}
            className="text-xs font-bold text-mission hover:opacity-80"
          >
            Responder
          </button>

          {canDeleteComment && (
            <button
              type="button"
              onClick={() => onDelete(comment.id)}
              aria-label="Excluir comentário"
              className="text-neutral-dark transition-colors hover:text-danger"
            >
              <FiTrash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
